import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Search, ShoppingBag } from 'lucide-react';
import type { StorefrontProduct } from '@mlm/shared';
import { fmtPeso, productImageUrl, store } from '../lib/api';
import { qk } from '../lib/queries';
import { cartCount, useCartStore } from '../lib/cart-store';
import {
  CoachButton,
  Eyebrow,
  ErrorNote,
  Loading,
  Screen,
  inputClass,
} from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

/**
 * The catalogue. Deliberately plain: a search box and a grid of cards, priced by
 * the server against the member's own slot. Everything that decides a price —
 * tier, rank, per-product segments — lives server-side, so this screen never
 * computes money, it only renders what `/storefront/products` returned.
 */
export function ShopPage() {
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
  });
  const slotId = ctx.data?.slotId ?? null;
  const catalog = useQuery({
    queryKey: qk.shopGoods(search),
    queryFn: () => store.catalog({ search: search || undefined, slotId, excludeCourses: true }),
    // Wait for the slot: pricing without it is priced against the member's
    // EARLIEST slot, which can differ from the one they actually buy on.
    enabled: !ctx.isPending,
    staleTime: 60_000,
  });
  const lines = useCartStore((s) => s.lines);
  const count = cartCount(lines);

  return (
    <Screen
      eyebrow="Shop"
      title="The store"
      subtitle="Everything the company sells, priced for your membership."
      wide
      aside={
        count > 0 ? (
          <Link to="/cart" className="block">
            <CoachButton kind="gold" block>
              <ShoppingBag className="h-4 w-4" aria-hidden />
              {count === 1 ? '1 item in cart' : `${count} items in cart`}
            </CoachButton>
          </Link>
        ) : null
      }
    >
      <ShopNav />
      <div className="border-b border-border px-5 py-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(term.trim());
          }}
          className="flex gap-2"
        >
          <label className="sr-only" htmlFor="shop-search">
            Search products
          </label>
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sage-deep"
              aria-hidden
            />
            <input
              id="shop-search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search products"
              className={`${inputClass} pl-9`}
            />
          </div>
          <CoachButton type="submit">Search</CoachButton>
        </form>
      </div>

      {catalog.isPending ? (
        <Loading label="Loading the store" />
      ) : catalog.error ? (
        <ErrorNote error={catalog.error} retry={() => void catalog.refetch()} />
      ) : (catalog.data?.products.length ?? 0) === 0 ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>Nothing here</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">
            {search ? `No product matches "${search}".` : 'No products are on sale right now.'}
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-px bg-border lg:grid-cols-3">
          {catalog.data!.products.map((p) => (
            <li key={p.id} className="bg-background">
              <ProductCard product={p} />
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}

function ProductCard({ product }: { product: StorefrontProduct }) {
  const soldOut = product.stockQty !== null && product.stockQty <= 0;
  const discounted =
    product.appliedDiscountPct !== '0.0000' && product.priceBase !== product.priceEffective;
  return (
    <Link
      to={`/shop/${product.id}`}
      className="flex h-full flex-col p-4 transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde"
    >
      <div className="relative aspect-square w-full overflow-hidden border border-border bg-surface">
        {product.images.length > 0 ? (
          <img
            src={productImageUrl(product.id, 0)}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sage-deep">
            <ImageOff className="h-6 w-6" aria-hidden />
          </div>
        )}
        {soldOut && (
          <span className="absolute inset-x-0 bottom-0 bg-ink/80 py-1 text-center font-mono text-[0.58rem] uppercase tracking-[0.12em] text-canvas">
            Sold out
          </span>
        )}
      </div>
      <p className="mt-3 line-clamp-2 text-[0.93rem] leading-snug text-ink">{product.name}</p>
      <div className="mt-auto pt-2">
        <p className="font-display text-[1.05rem] font-semibold text-ink">
          {fmtPeso(product.priceEffective)}
        </p>
        {discounted && (
          <p className="text-xs text-sage-deep line-through">{fmtPeso(product.priceBase)}</p>
        )}
        {product.freeShipping && <Eyebrow className="mt-1">Free shipping</Eyebrow>}
      </div>
    </Link>
  );
}
